// pages/ReportBuilder.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Play,
  Settings,
  Plus,
  Trash2,
  GripVertical,
} from "lucide-react";
import { useReports } from "../../contexts/ReportContext";

const ReportBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const {
    reportTypes,
    currentReport,
    fetchReport,
    fetchReportTypes,
    createReport,
    updateReport,
  } = useReports();

  const [reportData, setReportData] = useState({
    name: "",
    description: "",
    reportType: "",
    reportFormat: "tabular",
    folderId: null,
    columns: [],
    filters: [],
    groupings: [],
    aggregates: [],
    sorting: [],
    chartType: "none",
    chartConfig: null,
    scope: "user",
    isPublic: false,
  });

  const [activeTab, setActiveTab] = useState("columns");
  const [saving, setSaving] = useState(false);
  const [availableFields, setAvailableFields] = useState({});

  useEffect(() => {
    fetchReportTypes();
    if (isEditMode) {
      fetchReport(id);
    }
  }, [id]);

  useEffect(() => {
    if (currentReport && isEditMode) {
      setReportData(currentReport);
    }
  }, [currentReport, isEditMode]);

  useEffect(() => {
    if (reportData.reportType && reportTypes[reportData.reportType]) {
      setAvailableFields(reportTypes[reportData.reportType].fields);
    }
  }, [reportData.reportType, reportTypes]);

  const handleSave = async () => {
    try {
      setSaving(true);

      if (isEditMode) {
        await updateReport(id, reportData);
        alert("Report updated successfully");
      } else {
        const newReport = await createReport(reportData);
        navigate(`/reports/${newReport.id}`);
      }
    } catch (error) {
      alert("Failed to save report: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const addColumn = (field) => {
    if (!reportData.columns.find((c) => c.field === field)) {
      setReportData({
        ...reportData,
        columns: [
          ...reportData.columns,
          {
            field,
            label: availableFields[field]?.label || field,
          },
        ],
      });
    }
  };

  const removeColumn = (index) => {
    setReportData({
      ...reportData,
      columns: reportData.columns.filter((_, i) => i !== index),
    });
  };

  const addFilter = () => {
    setReportData({
      ...reportData,
      filters: [
        ...reportData.filters,
        {
          field: "",
          operator: "equals",
          value: "",
        },
      ],
    });
  };

  const updateFilter = (index, updates) => {
    setReportData({
      ...reportData,
      filters: reportData.filters.map((f, i) =>
        i === index ? { ...f, ...updates } : f,
      ),
    });
  };

  const removeFilter = (index) => {
    setReportData({
      ...reportData,
      filters: reportData.filters.filter((_, i) => i !== index),
    });
  };

  const addGrouping = (field) => {
    if (!reportData.groupings.find((g) => g.field === field)) {
      setReportData({
        ...reportData,
        groupings: [
          ...reportData.groupings,
          {
            field,
            label: availableFields[field]?.label || field,
          },
        ],
      });
    }
  };

  const removeGrouping = (index) => {
    setReportData({
      ...reportData,
      groupings: reportData.groupings.filter((_, i) => i !== index),
    });
  };

  const addAggregate = () => {
    setReportData({
      ...reportData,
      aggregates: [
        ...reportData.aggregates,
        {
          field: "",
          function: "SUM",
          label: "",
        },
      ],
    });
  };

  const updateAggregate = (index, updates) => {
    setReportData({
      ...reportData,
      aggregates: reportData.aggregates.map((a, i) =>
        i === index ? { ...a, ...updates } : a,
      ),
    });
  };

  const removeAggregate = (index) => {
    setReportData({
      ...reportData,
      aggregates: reportData.aggregates.filter((_, i) => i !== index),
    });
  };

  const getFilterableFields = () => {
    return Object.keys(availableFields).filter(
      (key) => availableFields[key].filterable,
    );
  };

  const getGroupableFields = () => {
    return Object.keys(availableFields).filter(
      (key) => availableFields[key].groupable,
    );
  };

  const getAggregatableFields = () => {
    return Object.keys(availableFields).filter(
      (key) => availableFields[key].aggregatable,
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0B0F] text-white p-4 lg:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/reports")}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditMode ? "Edit Report" : "New Report"}
            </h1>
            <p className="text-sm text-gray-400">
              Configure your custom report
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={saving || !reportData.name || !reportData.reportType}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={16} />
            <span>{saving ? "Saving..." : "Save Report"}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Configuration */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-6">
            <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Report Name *
                </label>
                <input
                  type="text"
                  value={reportData.name}
                  onChange={(e) =>
                    setReportData({ ...reportData, name: e.target.value })
                  }
                  placeholder="e.g., Monthly Sales Report"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Description
                </label>
                <textarea
                  value={reportData.description}
                  onChange={(e) =>
                    setReportData({
                      ...reportData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Describe what this report shows..."
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white resize-none focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Report Type *
                  </label>
                  <select
                    value={reportData.reportType}
                    onChange={(e) =>
                      setReportData({
                        ...reportData,
                        reportType: e.target.value,
                        columns: [],
                        filters: [],
                        groupings: [],
                        aggregates: [],
                      })
                    }
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select type...</option>
                    {Object.keys(reportTypes).map((type) => (
                      <option key={type} value={type}>
                        {reportTypes[type].label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Format
                  </label>
                  <select
                    value={reportData.reportFormat}
                    onChange={(e) =>
                      setReportData({
                        ...reportData,
                        reportFormat: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="tabular">Tabular</option>
                    <option value="summary">Summary</option>
                    <option value="matrix">Matrix</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Report Configuration Tabs */}
          {reportData.reportType && (
            <div className="bg-[#0C0E12] rounded-lg border border-gray-700">
              {/* Tabs */}
              <div className="flex border-b border-gray-700 overflow-x-auto">
                {["columns", "filters", "groupings", "aggregates", "chart"].map(
                  (tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                        activeTab === tab
                          ? "text-blue-400 border-b-2 border-blue-400"
                          : "text-gray-400 hover:text-white"
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ),
                )}
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {/* Columns Tab */}
                {activeTab === "columns" && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Selected Columns</h3>
                      <span className="text-sm text-gray-400">
                        {reportData.columns.length} columns
                      </span>
                    </div>

                    {reportData.columns.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <p>No columns selected</p>
                        <p className="text-sm mt-1">
                          Choose fields from the panel on the right
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {reportData.columns.map((column, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg"
                          >
                            <GripVertical
                              size={16}
                              className="text-gray-500 cursor-move"
                            />
                            <div className="flex-1">
                              <div className="font-medium">{column.label}</div>
                              <div className="text-xs text-gray-400">
                                {column.field}
                              </div>
                            </div>
                            <button
                              onClick={() => removeColumn(index)}
                              className="p-1 hover:bg-gray-700 rounded transition-colors"
                            >
                              <Trash2 size={14} className="text-red-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Filters Tab */}
                {activeTab === "filters" && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Filters</h3>
                      <button
                        onClick={addFilter}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition-colors"
                      >
                        <Plus size={14} />
                        Add Filter
                      </button>
                    </div>

                    {reportData.filters.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <p>No filters added</p>
                        <p className="text-sm mt-1">
                          Add filters to refine your report data
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {reportData.filters.map((filter, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-12 gap-3 p-3 bg-gray-800 rounded-lg"
                          >
                            <div className="col-span-4">
                              <select
                                value={filter.field}
                                onChange={(e) =>
                                  updateFilter(index, { field: e.target.value })
                                }
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                              >
                                <option value="">Select field...</option>
                                {getFilterableFields().map((field) => (
                                  <option key={field} value={field}>
                                    {availableFields[field].label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-span-3">
                              <select
                                value={filter.operator}
                                onChange={(e) =>
                                  updateFilter(index, {
                                    operator: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                              >
                                <option value="equals">Equals</option>
                                <option value="notEquals">Not Equals</option>
                                <option value="contains">Contains</option>
                                <option value="startsWith">Starts With</option>
                                <option value="greaterThan">
                                  Greater Than
                                </option>
                                <option value="lessThan">Less Than</option>
                                <option value="greaterOrEqual">≥</option>
                                <option value="lessOrEqual">≤</option>
                                <option value="in">In</option>
                                <option value="notIn">Not In</option>
                                <option value="isNull">Is Null</option>
                                <option value="isNotNull">Is Not Null</option>
                              </select>
                            </div>

                            <div className="col-span-4">
                              <input
                                type="text"
                                value={filter.value}
                                onChange={(e) =>
                                  updateFilter(index, { value: e.target.value })
                                }
                                placeholder="Value"
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                              />
                            </div>

                            <div className="col-span-1 flex items-center justify-center">
                              <button
                                onClick={() => removeFilter(index)}
                                className="p-1 hover:bg-gray-700 rounded transition-colors"
                              >
                                <Trash2 size={14} className="text-red-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Groupings Tab */}
                {activeTab === "groupings" && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Groupings</h3>
                      <span className="text-sm text-gray-400">
                        {reportData.groupings.length} groups
                      </span>
                    </div>

                    {reportData.groupings.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <p>No groupings added</p>
                        <p className="text-sm mt-1">
                          Choose groupable fields from the panel on the right
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {reportData.groupings.map((grouping, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg"
                          >
                            <GripVertical
                              size={16}
                              className="text-gray-500 cursor-move"
                            />
                            <div className="flex-1">
                              <div className="font-medium">
                                {grouping.label}
                              </div>
                              <div className="text-xs text-gray-400">
                                {grouping.field}
                              </div>
                            </div>
                            <button
                              onClick={() => removeGrouping(index)}
                              className="p-1 hover:bg-gray-700 rounded transition-colors"
                            >
                              <Trash2 size={14} className="text-red-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Aggregates Tab */}
                {activeTab === "aggregates" && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Aggregates</h3>
                      <button
                        onClick={addAggregate}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition-colors"
                      >
                        <Plus size={14} />
                        Add Aggregate
                      </button>
                    </div>

                    {reportData.aggregates.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <p>No aggregates added</p>
                        <p className="text-sm mt-1">
                          Add calculations like SUM, AVG, COUNT, etc.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {reportData.aggregates.map((aggregate, index) => (
                          <div
                            key={index}
                            className="grid grid-cols-12 gap-3 p-3 bg-gray-800 rounded-lg"
                          >
                            <div className="col-span-3">
                              <select
                                value={aggregate.function}
                                onChange={(e) =>
                                  updateAggregate(index, {
                                    function: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                              >
                                <option value="SUM">Sum</option>
                                <option value="AVG">Average</option>
                                <option value="COUNT">Count</option>
                                <option value="MIN">Minimum</option>
                                <option value="MAX">Maximum</option>
                                <option value="COUNT_DISTINCT">
                                  Count Distinct
                                </option>
                              </select>
                            </div>

                            <div className="col-span-4">
                              <select
                                value={aggregate.field}
                                onChange={(e) =>
                                  updateAggregate(index, {
                                    field: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                              >
                                <option value="">Select field...</option>
                                {getAggregatableFields().map((field) => (
                                  <option key={field} value={field}>
                                    {availableFields[field].label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-span-4">
                              <input
                                type="text"
                                value={aggregate.label || ""}
                                onChange={(e) =>
                                  updateAggregate(index, {
                                    label: e.target.value,
                                  })
                                }
                                placeholder="Label (optional)"
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                              />
                            </div>

                            <div className="col-span-1 flex items-center justify-center">
                              <button
                                onClick={() => removeAggregate(index)}
                                className="p-1 hover:bg-gray-700 rounded transition-colors"
                              >
                                <Trash2 size={14} className="text-red-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Chart Tab */}
                {activeTab === "chart" && (
                  <div>
                    <h3 className="font-semibold mb-4">Chart Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">
                          Chart Type
                        </label>
                        <select
                          value={reportData.chartType}
                          onChange={(e) =>
                            setReportData({
                              ...reportData,
                              chartType: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                        >
                          <option value="none">No Chart</option>
                          <option value="bar">Bar Chart</option>
                          <option value="column">Column Chart</option>
                          <option value="line">Line Chart</option>
                          <option value="pie">Pie Chart</option>
                          <option value="donut">Donut Chart</option>
                          <option value="funnel">Funnel Chart</option>
                        </select>
                      </div>

                      {reportData.chartType !== "none" && (
                        <div className="p-4 bg-gray-800 rounded-lg">
                          <p className="text-sm text-gray-400">
                            Chart will be displayed above the report data
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Available Fields */}
        <div className="lg:col-span-1">
          {reportData.reportType && (
            <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-4 sticky top-4">
              <h3 className="font-semibold mb-4">Available Fields</h3>

              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {Object.keys(availableFields).map((field) => {
                  const fieldInfo = availableFields[field];
                  const isSelected = reportData.columns.find(
                    (c) => c.field === field,
                  );

                  return (
                    <div
                      key={field}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-blue-600 text-white"
                          : "bg-gray-800 hover:bg-gray-700"
                      }`}
                      onClick={() => {
                        if (activeTab === "columns") {
                          addColumn(field);
                        } else if (
                          activeTab === "groupings" &&
                          fieldInfo.groupable
                        ) {
                          addGrouping(field);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium text-sm truncate">
                          {fieldInfo.label}
                        </div>
                        <div className="flex gap-1">
                          {fieldInfo.filterable && (
                            <span className="text-xs px-1 bg-blue-500/20 text-blue-400 rounded">
                              F
                            </span>
                          )}
                          {fieldInfo.groupable && (
                            <span className="text-xs px-1 bg-green-500/20 text-green-400 rounded">
                              G
                            </span>
                          )}
                          {fieldInfo.aggregatable && (
                            <span className="text-xs px-1 bg-orange-500/20 text-orange-400 rounded">
                              A
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">{field}</div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 p-3 bg-gray-800 rounded-lg text-xs">
                <div className="font-medium mb-2">Legend:</div>
                <div className="space-y-1 text-gray-400">
                  <div className="flex items-center gap-2">
                    <span className="px-1 bg-blue-500/20 text-blue-400 rounded">
                      F
                    </span>
                    <span>Filterable</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-1 bg-green-500/20 text-green-400 rounded">
                      G
                    </span>
                    <span>Groupable</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-1 bg-orange-500/20 text-orange-400 rounded">
                      A
                    </span>
                    <span>Aggregatable</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;
